import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { toast } from 'sonner';
import { ShieldAlert, ShieldOff } from 'lucide-react';
import TESTIDS from '@/constants/testIds';

export default function Automation() {
    const { brands, accounts, refresh, globalEmergencyStop, setGlobalEmergencyStop } = useScope();
    const [accountSettings, setAccountSettings] = useState({});
    const [safetyEvents, setSafetyEvents] = useState([]);

    const loadEvents = async () => {
        const r = await apiClient.get('/automation/safety_events');
        setSafetyEvents(r.data);
    };

    useEffect(() => {
        // pull current per-account automation snapshot
        const map = {};
        for (const a of accounts) {
            map[a.id] = { ...a.automation_settings };
        }
        setAccountSettings(map);
        loadEvents();
    }, [accounts]);

    const toggleGlobal = async (val) => {
        try {
            await apiClient.post('/automation/emergency_stop', { scope: 'global', enabled: val });
            setGlobalEmergencyStop(val);
            toast.success(`Global emergency stop ${val ? 'engaged' : 'lifted'}`);
        } catch (e) {
            toast.error('Failed to update');
        }
    };

    const toggleAccountStop = async (acc) => {
        try {
            await apiClient.post('/automation/emergency_stop', { scope: 'account', scope_id: acc.id, enabled: !acc.emergency_stop });
            await refresh();
            await loadEvents();
            toast.success(`Stop ${!acc.emergency_stop ? 'engaged' : 'lifted'} for @${acc.handle}`);
        } catch (e) {
            toast.error('Failed to toggle');
        }
    };

    const saveSettings = async (accountId) => {
        try {
            await apiClient.patch(`/automation/account/${accountId}`, { rules: accountSettings[accountId] });
            await refresh();
            toast.success('Saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Automation & safety</h1>
                <p className="text-sm text-muted-foreground">High-risk automation is OFF by default. Manual approval is the default for replies and DMs.</p>
            </div>
            <Card className="flex items-center justify-between border-border bg-card p-4">
                <div>
                    <div className="flex items-center gap-2 text-base font-semibold">
                        {globalEmergencyStop ? <ShieldOff className="h-4 w-4 text-[hsl(var(--danger))]" /> : <ShieldAlert className="h-4 w-4 text-[hsl(var(--warning))]" />}
                        Global emergency stop
                    </div>
                    <p className="text-xs text-muted-foreground">Halts publishing and scheduling across all accounts. Audit logged.</p>
                </div>
                <Switch checked={globalEmergencyStop} onCheckedChange={toggleGlobal} data-testid={TESTIDS.automationGlobalToggle} />
            </Card>

            <Card className="border-border bg-card">
                <div className="border-b border-border p-3">
                    <h2 className="text-base font-semibold">Per-account safety rules</h2>
                    <p className="text-xs text-muted-foreground">Each account holds its own automation snapshot. Adjust thresholds, rate limits, and review requirements here.</p>
                </div>
                <ul className="divide-y divide-border">
                    {accounts.length === 0 ? (
                        <li className="p-6 text-sm text-muted-foreground">No accounts to configure.</li>
                    ) : null}
                    {accounts.map((a) => {
                        const brand = brands.find((b) => b.id === a.brand_id);
                        const accent = brandColorVar(brand?.color_slot);
                        const cfg = accountSettings[a.id] || {};
                        const update = (key, value) => setAccountSettings((prev) => ({ ...prev, [a.id]: { ...(prev[a.id] || {}), [key]: value } }));
                        return (
                            <li
                                key={a.id}
                                className="grid grid-cols-1 gap-3 p-4 lg:grid-cols-[260px_1fr_120px]"
                                data-testid={TESTIDS.automationAccountRow}
                                style={{ '--brand-accent-current': accent }}
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: `hsl(${accent})` }} />
                                        <span className="font-mono text-sm">@{a.handle}</span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">{brand?.name}</div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <Switch
                                            checked={!!a.emergency_stop}
                                            onCheckedChange={() => toggleAccountStop(a)}
                                            data-testid={TESTIDS.automationToggleAccountStop}
                                        />
                                        <span className="text-xs">Account emergency stop</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                                    <div className="flex items-center justify-between rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <div>
                                            <div className="text-xs">Manual approval only</div>
                                            <div className="text-[10px] text-muted-foreground">Default: on</div>
                                        </div>
                                        <Switch
                                            checked={!!cfg.manual_approval_only}
                                            onCheckedChange={(v) => update('manual_approval_only', v)}
                                            data-testid={TESTIDS.automationToggleManualApproval}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <div>
                                            <div className="text-xs">Auto-reply safe categories</div>
                                            <div className="text-[10px] text-muted-foreground">Default: off</div>
                                        </div>
                                        <Switch
                                            checked={!!cfg.auto_reply_safe_categories}
                                            onCheckedChange={(v) => update('auto_reply_safe_categories', v)}
                                            data-testid={TESTIDS.automationToggleAutoReply}
                                        />
                                    </div>
                                    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <Label className="text-[11px]">Rate limit (hourly)</Label>
                                        <Input
                                            type="number"
                                            value={cfg.rate_limit_hourly ?? 12}
                                            onChange={(e) => update('rate_limit_hourly', Number(e.target.value))}
                                            className="mt-1 h-8"
                                        />
                                    </div>
                                    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <Label className="text-[11px]">Rate limit (daily)</Label>
                                        <Input
                                            type="number"
                                            value={cfg.rate_limit_daily ?? 100}
                                            onChange={(e) => update('rate_limit_daily', Number(e.target.value))}
                                            className="mt-1 h-8"
                                        />
                                    </div>
                                    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <Label className="text-[11px]">Per-user cooldown (min)</Label>
                                        <Input
                                            type="number"
                                            value={cfg.per_user_cooldown_minutes ?? 60}
                                            onChange={(e) => update('per_user_cooldown_minutes', Number(e.target.value))}
                                            className="mt-1 h-8"
                                        />
                                    </div>
                                    <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                                        <Label className="text-[11px]">Confidence threshold</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="1"
                                            value={cfg.confidence_threshold ?? 0.85}
                                            onChange={(e) => update('confidence_threshold', Number(e.target.value))}
                                            className="mt-1 h-8"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col items-end justify-between gap-2">
                                    <Button size="sm" onClick={() => saveSettings(a.id)}>Save</Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </Card>

            <Card className="border-border bg-card">
                <div className="border-b border-border p-3">
                    <h2 className="text-base font-semibold">Safety events</h2>
                    <p className="text-xs text-muted-foreground">Anything blocked, paused, or escalated lands here.</p>
                </div>
                {safetyEvents.length === 0 ? (
                    <div className="p-6 text-sm text-muted-foreground">No safety events recorded.</div>
                ) : (
                    <ul className="divide-y divide-border">
                        {safetyEvents.slice(0, 50).map((e) => (
                            <li key={e.id} className="px-4 py-2 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono">{e.type}</span>
                                    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                                        e.severity === 'high'
                                            ? 'bg-[hsl(var(--danger)/0.12)] text-[hsl(var(--danger))]'
                                            : e.severity === 'warning'
                                              ? 'bg-[hsl(var(--warning)/0.12)] text-[hsl(var(--warning))]'
                                              : 'bg-[hsl(var(--surface-3))] text-muted-foreground'
                                    }`}>{e.severity}</span>
                                </div>
                                <div className="text-muted-foreground">{e.summary}</div>
                                <div className="font-mono text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleString()}</div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
}
